import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
    Bell, 
    Check, 
    CheckCheck, 
    Calendar, 
    Clock, 
    Award, 
    AlertCircle,
    X
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from './ui/popover';
import { formatDistanceToNow } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const notificationIcons = {
    activity: Calendar,
    attendance: Clock,
    qualification: Award,
    system: AlertCircle
};

const NotificationItem = ({ notification, onMarkRead }) => {
    const Icon = notificationIcons[notification.notification_type] || Bell;
    const isUnread = !notification.read_by?.includes(notification.user_id);
    
    return (
        <div 
            className={`p-3 border-b border-border last:border-0 hover:bg-muted/50 transition-colors ${isUnread ? 'bg-accent/30' : ''}`}
        >
            <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${isUnread ? 'bg-primary/10' : 'bg-muted'}`}>
                    <Icon className={`w-4 h-4 ${isUnread ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`text-sm ${isUnread ? 'font-medium' : ''}`}>
                        {notification.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </p>
                </div>
                {isUnread && (
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={(e) => {
                            e.stopPropagation();
                            onMarkRead(notification.id);
                        }}
                    >
                        <Check className="w-3 h-3" />
                    </Button>
                )}
            </div>
        </div>
    );
};

export default function NotificationCenter({ userId }) {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    
    const fetchNotifications = useCallback(async () => {
        try {
            const [notifRes, countRes] = await Promise.all([
                axios.get(`${API_URL}/notifications`),
                axios.get(`${API_URL}/notifications/unread-count`)
            ]);
            setNotifications(notifRes.data);
            setUnreadCount(countRes.data.count);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    }, []);
    
    useEffect(() => {
        fetchNotifications();
        
        // Poll for new notifications every 30 seconds
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);
    
    // Request browser notification permission on mount
    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);
    
    // Show browser notification when unread count increases
    useEffect(() => {
        if (unreadCount > 0 && 'Notification' in window && Notification.permission === 'granted') {
            // Only show if page is not focused
            if (document.hidden) {
                const latestUnread = notifications.find(n => !n.read_by?.includes(userId));
                if (latestUnread) {
                    new Notification(latestUnread.title, {
                        body: latestUnread.message,
                        icon: '/favicon.ico',
                        tag: latestUnread.id
                    });
                }
            }
        }
    }, [unreadCount, notifications, userId]);
    
    const handleMarkRead = async (notifId) => {
        try {
            await axios.post(`${API_URL}/notifications/${notifId}/read`);
            fetchNotifications();
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };
    
    const handleMarkAllRead = async () => {
        try {
            await axios.post(`${API_URL}/notifications/read-all`);
            fetchNotifications();
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };
    
    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative"
                    data-testid="notifications-btn"
                >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center font-medium">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
                <div className="flex items-center justify-between p-3 border-b border-border">
                    <h3 className="font-semibold">Notifications</h3>
                    {unreadCount > 0 && (
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-xs h-7"
                            onClick={handleMarkAllRead}
                        >
                            <CheckCheck className="w-3 h-3 mr-1" />
                            Mark all read
                        </Button>
                    )}
                </div>
                
                <ScrollArea className="h-[300px]">
                    {notifications.length > 0 ? (
                        notifications.map((notification) => (
                            <NotificationItem
                                key={notification.id}
                                notification={{ ...notification, user_id: userId }}
                                onMarkRead={handleMarkRead}
                            />
                        ))
                    ) : (
                        <div className="p-8 text-center text-muted-foreground">
                            <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No notifications</p>
                        </div>
                    )}
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
}
